import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { Box, Typography, TextField, InputAdornment } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CommonSelect from "../../common/CommonSelect";
import { capitalizeFirstLetter } from "../../../utils/capitalize";

const stateMapping = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
};

const fullStates = Object.values(stateMapping).sort((a, b) =>
  a.localeCompare(b),
);

const textFieldSx = (disabled) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: disabled ? "#F2F2F2" : "#FFFFFF",
    borderRadius: "8px",
    height: "43px",
    "& fieldset": { border: disabled ? "none" : "1px solid #E8E8E9" },
  },
  "& .MuiInputBase-input": {
    fontSize: "14px",
    color: disabled ? "#999893" : "#0e1324",
    pr: 0,
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "#999893",
    fontSize: "14px",
  },
});

const labelSx = {
  fontSize: "13px",
  color: "#66638C",
  fontWeight: 500,
  mb: "8px",
  display: "block",
};

const dashIfEmpty = (disabled, value) =>
  disabled && !value ? "-" : value || "";

const counterSlotProps = (disabled, text, max) =>
  disabled
    ? undefined
    : {
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <Typography
                component="span"
                sx={{
                  fontSize: "12px",
                  color: "#666",
                  whiteSpace: "nowrap",
                  mr: 0.5,
                }}
              >
                {(text || "").length}/{max}
              </Typography>
            </InputAdornment>
          ),
        },
      };

const capitalizedOrDash = (disabled, value) => {
  if (!disabled) return value || "";
  return value ? capitalizeFirstLetter(value) : "-";
};

const limitedChange = (onChange, field, max) => (e) => {
  if (e.target.value.length <= max) onChange(field, e.target.value);
};

const PrescriberInfo = ({ data, onChange, disabled, errors = {} }) => {
  const stateAnchorRef = useRef(null);
  const [stateAnchorEl, setStateAnchorEl] = useState(null);

  const handleOpenStateSelect = (event) => {
    setStateAnchorEl(event.currentTarget);
  };

  const handleCloseStateSelect = () => {
    setStateAnchorEl(null);
  };

  const hasPrescriberData = !!(
    data.npi ||
    data.firstName ||
    data.lastName ||
    data.addressLine1 ||
    data.city ||
    data.stateProvince ||
    data.postalCode ||
    data.phone ||
    data.fax
  );

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography
        sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F", mb: 1 }}
      >
        Prescriber Information
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
        <Typography sx={labelSx}>NPI (National Provider Identifier)</Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Enter NPI"
          value={dashIfEmpty(disabled, data.npi)}
          disabled={disabled} // Apply disabled prop
          error={!!errors.npi}
          helperText={errors.npi}
          onChange={limitedChange(onChange, "npi", 10)}
          slotProps={counterSlotProps(disabled, data.npi, 10)}
          sx={textFieldSx(disabled)}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <Typography sx={labelSx}>First Name</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter first name"
            value={capitalizedOrDash(disabled, data.firstName)}
            disabled={disabled} // Apply disabled prop
            error={!!errors.firstName}
            helperText={errors.firstName}
            onChange={limitedChange(onChange, "firstName", 32)}
            slotProps={counterSlotProps(disabled, data.firstName, 32)}
            sx={textFieldSx(disabled)}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <Typography sx={labelSx}>Last Name</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter last name"
            value={capitalizedOrDash(disabled, data.lastName)}
            disabled={disabled} // Apply disabled prop
            error={!!errors.lastName}
            helperText={errors.lastName}
            onChange={limitedChange(onChange, "lastName", 32)}
            slotProps={counterSlotProps(disabled, data.lastName, 32)}
            sx={textFieldSx(disabled)}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
        <Typography sx={labelSx}>Street</Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Enter street address"
          value={capitalizedOrDash(disabled, data.addressLine1)}
          disabled={disabled} // Apply disabled prop
          error={!!errors.addressLine1}
          helperText={errors.addressLine1}
          onChange={limitedChange(onChange, "addressLine1", 60)}
          slotProps={counterSlotProps(disabled, data.addressLine1, 60)}
          sx={textFieldSx(disabled)}
        />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
        <Typography sx={labelSx}>Line 2</Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Enter line 2"
          value={capitalizedOrDash(disabled, data.addressLine2)}
          disabled={disabled} // Apply disabled prop
          error={!!errors.addressLine2}
          helperText={errors.addressLine2}
          onChange={limitedChange(onChange, "addressLine2", 60)}
          slotProps={counterSlotProps(disabled, data.addressLine2, 60)}
          sx={textFieldSx(disabled)}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box
          sx={{
            flex: 2,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <Typography sx={labelSx}>City</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter city"
            value={capitalizedOrDash(disabled, data.city)}
            disabled={disabled} // Apply disabled prop
            error={!!errors.city}
            helperText={errors.city}
            onChange={limitedChange(onChange, "city", 50)}
            slotProps={counterSlotProps(disabled, data.city, 50)}
            sx={textFieldSx(disabled)}
          />
        </Box>
        <Box
          sx={{
            flex: 2,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <Typography sx={labelSx}>State</Typography>
          <Box
            ref={stateAnchorRef}
            onClick={disabled ? undefined : handleOpenStateSelect}
            sx={{
              width: "100%",
              height: "43px",
              border: disabled ? "none" : "1px solid",
              borderColor: errors.stateProvince ? "#d32f2f" : "#E8E8E9",
              borderRadius: "8px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0 12px",
              backgroundColor: disabled ? "#F2F2F2" : "#FFFFFF",
              cursor: disabled ? "default" : "pointer",
              "&:hover": {
                backgroundColor: disabled ? "#F2F2F2" : "#F9FAFB",
              },
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                color: !disabled && data.stateProvince ? "#0e1324" : "#999893",
              }}
            >
              {stateMapping[data.stateProvince] ||
                data.stateProvince ||
                (disabled ? "-" : "Select State")}
            </Typography>
            <KeyboardArrowDownIcon
              sx={{
                color: disabled ? "#C4C2D3" : "#6B7280",
                fontSize: "20px",
                transition: "transform 0.2s",
                transform: stateAnchorEl ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </Box>
          {errors.stateProvince && (
            <Typography
              sx={{
                color: "#d32f2f",
                fontSize: "0.75rem",
                mt: "3px",
                ml: "14px",
              }}
            >
              {errors.stateProvince}
            </Typography>
          )}
          <CommonSelect
            id="prescriber-state-select"
            open={!!stateAnchorEl}
            anchorEl={stateAnchorEl}
            onClose={handleCloseStateSelect}
            noteTypes={fullStates}
            selectedTypes={[
              stateMapping[data.stateProvince] || data.stateProvince,
            ]}
            handleTypeToggle={(type) => {
              const code = Object.keys(stateMapping).find(
                (key) => stateMapping[key] === type,
              );
              onChange("stateProvince", code || type);
              handleCloseStateSelect();
            }}
            width={
              stateAnchorRef.current?.offsetWidth
                ? `${stateAnchorRef.current.offsetWidth}px`
                : "240px"
            }
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <Typography sx={labelSx}>
            Zip Code{" "}
            {hasPrescriberData && <span style={{ color: "#d32f2f" }}>*</span>}
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter zip code"
            value={dashIfEmpty(disabled, data.postalCode)}
            disabled={disabled} // Apply disabled prop
            error={!!errors.postalCode}
            helperText={errors.postalCode}
            onChange={limitedChange(onChange, "postalCode", 10)}
            slotProps={counterSlotProps(disabled, data.postalCode, 10)}
            sx={textFieldSx(disabled)}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <Typography sx={labelSx}>Phone Number</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter phone number"
            value={dashIfEmpty(disabled, data.phone)}
            error={!!errors.phone}
            helperText={errors.phone}
            disabled={disabled} // Apply disabled prop
            onChange={(e) => {
              const sanitized = e.target.value
                .replaceAll(/\D/g, "")
                .slice(0, 10);
              onChange("phone", sanitized);
            }}
            slotProps={counterSlotProps(disabled, data.phone, 10)}
            sx={textFieldSx(disabled)}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <Typography sx={labelSx}>Fax Number</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter fax number"
            value={dashIfEmpty(disabled, data.fax)}
            disabled={disabled} // Apply disabled prop
            error={!!errors.fax}
            helperText={errors.fax}
            onChange={limitedChange(onChange, "fax", 14)}
            slotProps={counterSlotProps(disabled, data.fax, 14)}
            sx={textFieldSx(disabled)}
          />
        </Box>
      </Box>
    </Box>
  );
};

PrescriberInfo.propTypes = {
  data: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool, // Add disabled to propTypes
  errors: PropTypes.object,
};

export default PrescriberInfo;
